import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ProvidersService } from '../../../../../js/api';
import { useCurrentUser } from '../../../../../js/app/hooks/auth';
import { useProvider } from '../../../../../js/app/hooks/providers';
import { ContractSigningModal } from '../../../components/contract-signing-modal';

type PendingTask = {
  id: string;
  label: string;
  onClick: () => void;
};

export function ProfileCompletionTasks() {
  const { user } = useCurrentUser();
  const { provider } = useProvider();
  const navigate = useNavigate();

  // Contract signing state
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractText, setContractText] = useState<string | null>(null);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [isSigningContract, setIsSigningContract] = useState(false);
  const [isDownloadingContract, setIsDownloadingContract] = useState(false);

  // Contract handling functions
  const handleContractRetrieveClick = useCallback(async () => {
    if (!user?.provider_id) return;

    setIsLoadingContract(true);
    setShowContractModal(true);

    try {
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await ProvidersService.providersContractRetrieve({
        id: user.provider_id,
        xTz,
      });
      setContractText(response.text);
    } catch (error) {
      console.error('Error retrieving contract:', error);
      toast.error('Failed to load contract. Please try again.');
    } finally {
      setIsLoadingContract(false);
    }
  }, [user?.provider_id]);

  const handleDownloadContract = async () => {
    if (!user?.provider_id) return;

    setIsDownloadingContract(true);
    try {
      const xTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await ProvidersService.providersContractRetrieve({
        id: user.provider_id,
        xTz,
      });

      // Create a blob and download
      const blob = new Blob([response], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'professional-services-agreement.html';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Failed to download contract. Please try again.');
    } finally {
      setIsDownloadingContract(false);
    }
  };

  const handleContractAgree = async (firstName: string, lastName: string) => {
    if (!user?.provider_id) return;

    setIsSigningContract(true);
    try {
      await ProvidersService.providersContractCreate({
        id: user.provider_id,
        xTz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        requestBody: {
          signed_first_name: firstName,
          signed_last_name: lastName,
        },
      });

      toast.success('Contract signed successfully!');
      setShowContractModal(false);
      setContractText(null);

      // Refresh the page to update the user's contract status
      window.location.reload();
    } catch (error) {
      console.error('Error signing contract:', error);
      toast.error('Failed to sign contract. Please try again.');
    } finally {
      setIsSigningContract(false);
    }
  };

  const pendingTasks: PendingTask[] = useMemo(() => {
    if (!user || !provider) return [];

    // Use provider data for profile completion checks since this is the source of truth
    // The /api/provider/current endpoint contains all the credentialing data
    const hasCv = Boolean(provider.cv);
    const hasReferences = Array.isArray(provider.references) && provider.references.length > 0;
    const professionalTraitsComplete = hasCv && hasReferences;

    const licenses: any[] = Array.isArray(provider.licenses) ? provider.licenses : []; // eslint-disable-line @typescript-eslint/no-explicit-any
    const licensesComplete = licenses.some(
      (license) => license?.type === 'MEDICAL' && Boolean(license?.file)
    );

    const questionnaireComplete = Boolean(provider.completed_questionnaire);

    // Complete when needs_contract_signed is falsy (empty string or null)
    const serviceAgreementComplete = !provider.needs_contract_signed;

    const tasks: PendingTask[] = [];

    if (!professionalTraitsComplete) {
      tasks.push({
        id: 'professional-traits',
        label: 'CV and Reference(s)',
        onClick: () => navigate('/app/provider/edit/professional-traits'),
      });
    }

    if (!licensesComplete) {
      tasks.push({
        id: 'licenses',
        label: 'Medical and DEA Licenses',
        onClick: () => navigate('/app/provider/edit/licenses'),
      });
    }

    if (!questionnaireComplete) {
      tasks.push({
        id: 'questionnaire',
        label: 'Provider Questionnaire',
        onClick: () => navigate(`/app/provider/questionnaire/${user.provider_id}`),
      });
    }

    if (!serviceAgreementComplete) {
      tasks.push({
        id: 'service-agreement',
        label: 'Service Agreement',
        onClick: handleContractRetrieveClick,
      });
    }

    return tasks;
  }, [user, provider, navigate, handleContractRetrieveClick]);

  if (pendingTasks.length === 0) {
    return null;
  }

  return (
    <>
      <div className="tw:bg-white tw:rounded-xl tw:p-4 tw:border tw:border-gray-200">
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div className="tw:font-medium">Complete Your Profile</div>
          <div className="tw:flex tw:flex-col tw:gap-4">
            {pendingTasks.map((task) => (
              <div key={task.id} className="tw:flex tw:items-center tw:justify-between">
                <button
                  onClick={task.onClick}
                  className="tw:flex tw:items-center tw:gap-3 tw:cursor-pointer tw:bg-transparent tw:border-none tw:p-0 tw:text-left tw:w-full"
                  type="button"
                >
                  <i className="fa-solid fa-circle-arrow-right tw:text-orange-500" />
                  <div className="tw:text-sm">{task.label}</div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ContractSigningModal
        isOpen={showContractModal}
        onClose={() => setShowContractModal(false)}
        contractText={contractText}
        isLoadingContract={isLoadingContract}
        isSigningContract={isSigningContract}
        isDownloadingContract={isDownloadingContract}
        onDownloadContract={handleDownloadContract}
        onSignContract={handleContractAgree}
      />
    </>
  );
}

export default ProfileCompletionTasks;
